import { useQuery } from '@apollo/client';
import { useSearchParams } from 'next/navigation';

import { LoadingSpinner, Text } from '@thinkific/chalk';

import { AuthCheck, AuthCheckQueryData } from '@/queries/AuthCheck';

import styles from './Auth.module.scss';

/**
 * The `Auth` page performs an authorization check using the `AuthCheck` query. If your
 * `graphql_token` has expired, this page will automatically perform a client-side refresh
 * before taking you to your desired destination.
 *
 * If authorization fails even with the refresh token, the user will be redirected to the
 * Thinkific sign-in page.
 */
const Auth = () => {
  const searchParams = useSearchParams();
  const returnTo = searchParams?.get('return_to') || '/manage';

  const { data } = useQuery<AuthCheckQueryData>(AuthCheck, {
    context: {
      clientName: 'public',
    },
    fetchPolicy: 'no-cache',
    onError: () => {
      // Redirect to the sign-in page with an error message
      // if we are unable to authorize the user.
      const params = new URLSearchParams();
      params.set('redirect_to', returnTo);
      params.set(
        'error',
        'An error occurred while attempting to connect your account'
      );

      window.location.href = `/users/sign_in?${params.toString()}`;
    },
  });

  if (data?.me) {
    // Authorized, can redirect where we were trying to go.
    window.location.href = returnTo;
  }

  return (
    <div className={styles.auth}>
      <LoadingSpinner />
      <Text>Just a moment...</Text>
    </div>
  );
};

export default Auth;
